import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, UserPlus, UserCheck, MessageCircle, Loader2, Heart, MapPin,
  Briefcase, Camera, Quote, Calendar,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import DMModal from "../components/community/DMModal";
import MembershipGuardModal, { canSendDM } from "../components/MembershipGuardModal";
import { safeFetch } from "../utils/safeFetch";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 새 SNS 9개 카테고리 (좌측 사이드바와 동일) — 감성 타임라인 필터용
const TIMELINE_CATEGORIES = [
  { id: "all", label: "전체", emoji: "✨" },
  { id: "home", label: "홈피드", emoji: "🏠" },
  { id: "relationships", label: "연애&인간관계", emoji: "💗" },
  { id: "travel", label: "여행", emoji: "✈️" },
  { id: "health", label: "건강", emoji: "🌿" },
  { id: "hobby", label: "취미", emoji: "🎨" },
  { id: "retirement", label: "노후&인생2막", emoji: "🌅" },
  { id: "free", label: "자유게시판", emoji: "💬" },
  { id: "today_moment", label: "오늘의 하루", emoji: "📝" },
  { id: "anonymous_advice", label: "익명 고민상담", emoji: "🤫" },
];

const TOPIC_LABELS = {
  today_feeling: "오늘의 감정",
  today_question: "오늘의 질문",
  someones_story: "누군가의 이야기",
  lonely_night: "외로운 밤의 대화",
  global_korean: "글로벌 한인 싱글",
  liz_words: "리즈의 한마디",
  todays_empathy: "오늘의 공감글",
  midlife_love: "중년의 사랑 이야기",
  quiet_counsel: "조용한 고민상담",
};

const REACTION_EMOJI = {
  empathy: "🤝", support: "💪", flutter: "💗",
  comfort: "🌿", metoo: "🫶", goodperson: "✨",
};

function monthKey(iso) {
  if (!iso) return "기타";
  const d = new Date(iso);
  return `${d.getFullYear()}년 ${d.getMonth() + 1}월`;
}

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "방금 전";
  if (diff < 3600) return `${Math.floor(diff / 60)}분 전`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}시간 전`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}일 전`;
  return new Date(iso).toLocaleDateString("ko-KR", { month: "short", day: "numeric" });
}

function groupByMonth(items, dateKey = "created_at") {
  const map = new Map();
  for (const it of items) {
    const key = monthKey(it[dateKey]);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(it);
  }
  return Array.from(map.entries());
}

// =========================================================================
//  Tabs
// =========================================================================
const TABS = [
  { id: "posts", label: "이야기", icon: Quote },
  { id: "comments", label: "댓글", icon: MessageCircle },
  { id: "photos", label: "사진", icon: Camera },
];

// =========================================================================
//  Post Timeline Item
// =========================================================================
function TimelinePost({ p }) {
  const navigate = useNavigate();
  const counts = useMemo(() => {
    const total = Object.values(p.reactions || {}).reduce((s, arr) => s + (arr?.length || 0), 0);
    const top = Object.entries(p.reactions || {})
      .map(([k, arr]) => ({ k, n: arr?.length || 0 }))
      .filter((x) => x.n > 0)
      .sort((a, b) => b.n - a.n)
      .slice(0, 3);
    return { total, top };
  }, [p.reactions]);
  const topicLabel = TOPIC_LABELS[p.topic];
  return (
    <motion.article
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-3xl p-5 sm:p-6 border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.02)]"
      data-testid={`timeline-post-${p.id}`}
    >
      <div className="flex items-center gap-2 text-xs text-stone-400 mb-3">
        <Calendar className="w-3.5 h-3.5" />
        <span>{timeAgo(p.created_at)}</span>
        {topicLabel && (
          <>
            <span>·</span>
            <span className="text-burgundy-600 font-medium">{topicLabel}</span>
          </>
        )}
      </div>
      <p className="text-[17px] text-stone-800 leading-[1.85] whitespace-pre-wrap break-words mb-4">
        {p.content}
      </p>
      {p.images?.length > 0 && (
        <div className={`grid gap-1 mb-4 rounded-2xl overflow-hidden ${p.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {p.images.slice(0, 4).map((url, i) => (
            <img
              key={i}
              src={url.startsWith("http") ? url : `${API_URL}${url}`}
              alt=""
              className="w-full h-44 object-cover"
            />
          ))}
        </div>
      )}
      <div className="flex items-center justify-between text-xs text-stone-500">
        <div className="flex items-center gap-2">
          {counts.top.map((t) => (
            <span key={t.k} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-stone-50 border border-stone-100">
              <span className="text-sm">{REACTION_EMOJI[t.k] || "💗"}</span>
              <span className="font-medium tabular-nums">{t.n}</span>
            </span>
          ))}
        </div>
        <button
          onClick={() => navigate(`/community/post/${p.id}`)}
          className="flex items-center gap-1.5 text-stone-500 hover:text-burgundy-700 font-medium"
        >
          <MessageCircle className="w-3.5 h-3.5" /> 댓글 {p.comments || 0}
        </button>
      </div>
    </motion.article>
  );
}

// =========================================================================
//  Comment Timeline Item
// =========================================================================
function TimelineComment({ c }) {
  const navigate = useNavigate();
  const isAnon = c.post_is_anonymous;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className="bg-white rounded-3xl p-5 border border-stone-100"
      data-testid={`timeline-comment-${c.id}`}
    >
      <div className="text-xs text-stone-400 flex items-center gap-2 mb-2">
        <Calendar className="w-3.5 h-3.5" />
        <span>{timeAgo(c.created_at)}</span>
        <span>·</span>
        <span className="text-stone-500">
          {c.commenter_name
            ? `${c.commenter_name} 님이 회원님의 글에`
            : `${isAnon ? "익명 글" : (c.post_author || "")}의 글에`}
        </span>
      </div>
      {c.post_title && (
        <button
          onClick={() => navigate(`/community/post/${c.post_id}`)}
          className="block text-left mb-3 px-3 py-2 rounded-xl bg-stone-50 text-stone-500 text-sm leading-relaxed border-l-2 border-stone-200 hover:bg-stone-100 transition-colors w-full truncate"
        >
          "{c.post_title}"
        </button>
      )}
      <p className="text-[15px] text-stone-800 leading-[1.75] whitespace-pre-wrap break-words">
        {c.content}
      </p>
    </motion.div>
  );
}

// =========================================================================
//  Empty State
// =========================================================================
function Empty({ label }) {
  return (
    <div className="py-20 text-center">
      <Heart className="w-8 h-8 mx-auto text-stone-300 mb-3" strokeWidth={1.5} />
      <p className="font-serif text-lg text-stone-500">{label}</p>
      <p className="text-sm text-stone-400 mt-2">새로운 이야기가 쌓이면 이곳에 보여요 🌿</p>
    </div>
  );
}

// =========================================================================
//  Main Page
// =========================================================================
export default function EmotionalTimelinePage() {
  const { userId: paramUserId } = useParams();
  const navigate = useNavigate();
  const { user: me, token } = useAuth();
  const userId = paramUserId || me?.id;
  const isMe = me && userId === me.id;

  const [profile, setProfile] = useState(null);
  const [followStatus, setFollowStatus] = useState(null);
  const [tab, setTab] = useState("posts");
  const [posts, setPosts] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followBusy, setFollowBusy] = useState(false);
  const [dmOpen, setDmOpen] = useState(false);
  const [dmGuard, setDmGuard] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("all"); // 감성 타임라인 카테고리 필터
  const dmAllowed = canSendDM(me);

  // Fetch profile + follow status
  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    Promise.all([
      fetch(`${API_URL}/api/users/${userId}/public-profile`).then((r) => r.ok ? r.json() : null),
      fetch(`${API_URL}/api/follows/status/${userId}`, { headers }).then((r) => r.ok ? r.json() : null),
    ])
      .then(([prof, status]) => {
        setProfile(prof);
        setFollowStatus(status);
      })
      .finally(() => setLoading(false));
  }, [userId, token]);

  // 프로필 방문 기록 (로그인 상태 + 타인 프로필 열람 시)
  useEffect(() => {
    if (!userId || !token || isMe) return;
    fetch(`${API_URL}/api/users/${userId}/visit`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }).catch(() => { /* silent */ });
  }, [userId, token, isMe]);

  // Fetch posts
  useEffect(() => {
    if (!userId) return;
    fetch(`${API_URL}/api/posts/by-author/${userId}?limit=100`)
      .then((r) => r.ok ? r.json() : { posts: [] })
      .then((d) => setPosts(d.posts || []));
  }, [userId]);

  // Fetch comments (lazy when tab is comments)
  useEffect(() => {
    if (tab !== "comments" || !userId || comments.length > 0) return;
    fetch(`${API_URL}/api/comments/on-author-posts/${userId}?limit=100`)
      .then((r) => r.ok ? r.json() : { comments: [] })
      .then((d) => setComments(d.comments || []));
  }, [tab, userId, comments.length]);

  const photoItems = useMemo(() => {
    const items = [];
    for (const p of posts) {
      if (p.images?.length) {
        for (const url of p.images) {
          items.push({ url, postId: p.id, created_at: p.created_at });
        }
      }
    }
    return items;
  }, [posts]);

  // 카테고리별 필터링 + 카운트 (Hooks must run before any early returns)
  const categoryCounts = useMemo(() => {
    const counts = { all: posts.length };
    for (const p of posts) {
      const c = p.category || "home";
      counts[c] = (counts[c] || 0) + 1;
    }
    return counts;
  }, [posts]);
  const filteredPosts = useMemo(() => {
    if (categoryFilter === "all") return posts;
    return posts.filter((p) => (p.category || "home") === categoryFilter);
  }, [posts, categoryFilter]);

  const toggleFollow = async () => {
    if (!me) { toast.error("로그인이 필요합니다"); return; }
    if (isMe) return;
    setFollowBusy(true);
    try {
      const res = await safeFetch(`${API_URL}/api/follows/${userId}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "실패");
      setFollowStatus((s) => ({
        ...(s || {}),
        i_follow: data.following,
        followers_count: data.followers_count,
        mutual: data.following && s?.they_follow_me,
      }));
      toast.success(data.following ? "팔로우했어요 💞" : "팔로우 취소");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-burgundy-500" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen pt-24 px-6 text-center">
        <p className="font-serif text-xl text-stone-600">유저를 찾을 수 없어요.</p>
        <button onClick={() => navigate(-1)} className="mt-4 text-burgundy-600 underline">돌아가기</button>
      </div>
    );
  }

  const groupedPosts = groupByMonth(filteredPosts);
  const groupedComments = groupByMonth(comments);

  return (
    <div className="min-h-screen bg-[#F9F8F6] pt-20 pb-32" data-testid="emotional-timeline-page">
      {/* Header / Hero */}
      <div className="relative bg-gradient-to-br from-rose-50 via-amber-50 to-stone-50 border-b border-stone-100">
        <div className="absolute inset-0 grain-overlay pointer-events-none opacity-50" />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 relative">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-stone-500 hover:text-stone-800 mb-5"
            data-testid="timeline-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> 뒤로
          </button>
          <div className="flex items-start gap-5">
            {profile.profile_image ? (
              <img
                src={profile.profile_image.startsWith("http") ? profile.profile_image : `${API_URL}${profile.profile_image}`}
                alt=""
                className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-4 border-white shadow-sm shrink-0"
              />
            ) : (
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-gradient-to-br from-burgundy-500 to-rose-500 text-white flex items-center justify-center font-bold text-3xl shadow-sm shrink-0 border-4 border-white">
                {(profile.username || "?").charAt(0)}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="font-serif text-2xl sm:text-3xl text-stone-900 tracking-tight truncate" data-testid="timeline-username">
                {profile.username}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-sm text-stone-500">
                {profile.age && <span>{profile.age}세</span>}
                {(profile.gender === "male" || profile.gender === "female") && (
                  <span data-testid="timeline-gender">{profile.gender === "male" ? "남성" : "여성"}</span>
                )}
                {profile.city && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{profile.city}</span>}
                {profile.job && <span className="inline-flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{profile.job}</span>}
              </div>
              {profile.introduction && (
                <p className="font-serif text-[15px] text-stone-700 leading-relaxed mt-3 line-clamp-3">
                  {profile.introduction}
                </p>
              )}
            </div>
          </div>

          {/* Stats */}
          {followStatus && (
            <div className="flex gap-5 mt-5 pl-1">
              <Link to={`/timeline/${userId}/followers`} className="block hover:opacity-70 transition-opacity" data-testid="link-followers">
                <div className="text-xl font-bold text-stone-900 tabular-nums" data-testid="stat-followers">{followStatus.followers_count}</div>
                <div className="text-xs text-stone-500 mt-0.5">팔로워</div>
              </Link>
              <Link to={`/timeline/${userId}/following`} className="block hover:opacity-70 transition-opacity" data-testid="link-following">
                <div className="text-xl font-bold text-stone-900 tabular-nums" data-testid="stat-following">{followStatus.following_count}</div>
                <div className="text-xs text-stone-500 mt-0.5">팔로잉</div>
              </Link>
              <div>
                <div className="text-xl font-bold text-stone-900 tabular-nums" data-testid="stat-posts">{posts.length}</div>
                <div className="text-xs text-stone-500 mt-0.5">이야기</div>
              </div>
            </div>
          )}

          {/* Actions */}
          {!isMe && me && (
            <div className="flex gap-2 mt-5">
              <button
                onClick={toggleFollow}
                disabled={followBusy}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-[15px] transition-colors disabled:opacity-50 ${
                  followStatus?.i_follow
                    ? "bg-white text-stone-700 border border-stone-200 hover:bg-stone-50"
                    : "bg-burgundy-600 text-white hover:bg-burgundy-700"
                }`}
                data-testid="timeline-follow-btn"
              >
                {followBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  followStatus?.i_follow ? <><UserCheck className="w-4 h-4" /> 팔로잉</> : <><UserPlus className="w-4 h-4" /> 팔로우</>
                )}
              </button>
              <button
                onClick={() => {
                  if (!me) { setDmGuard("auth"); return; }
                  if (!dmAllowed) { setDmGuard("dm_upgrade"); return; }
                  setDmOpen(true);
                }}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-[15px] border ${
                  dmAllowed
                    ? "bg-white text-stone-700 border-stone-200 hover:bg-stone-50"
                    : "bg-stone-50 text-stone-500 border-stone-200 hover:bg-stone-100"
                }`}
                data-testid="timeline-dm-btn"
              >
                <MessageCircle className="w-4 h-4" /> 쪽지
                {!dmAllowed && me && (
                  <span className="text-[10px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded ml-0.5">정회원</span>
                )}
              </button>
            </div>
          )}
          {isMe && (
            <div className="mt-5">
              <Link
                to="/profile"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-stone-700 text-sm font-semibold border border-stone-200 hover:bg-stone-50"
              >
                프로필 편집
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-16 z-10 bg-[#F9F8F6]/95 backdrop-blur-sm border-b border-stone-100">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1" data-testid="timeline-tabs">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-[14px] font-semibold border-b-2 transition-colors ${
                    active
                      ? "border-burgundy-600 text-burgundy-700"
                      : "border-transparent text-stone-500 hover:text-stone-800"
                  }`}
                  data-testid={`timeline-tab-${t.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        {tab === "posts" && (
          <>
            {/* 카테고리 필터 칩 — 감성 타임라인의 핵심 차별화 */}
            {posts.length > 0 && (
              <div className="mb-6" data-testid="timeline-category-chips">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 bg-burgundy-500 rounded-full" />
                  <h3 className="text-[13px] font-bold text-stone-700 tracking-wide">감성 카테고리</h3>
                  <span className="text-[11px] text-stone-400">· 카테고리별로 이야기를 모아봤어요</span>
                </div>
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1 [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
                  {TIMELINE_CATEGORIES.map((cat) => {
                    const count = categoryCounts[cat.id] || 0;
                    if (cat.id !== "all" && count === 0) return null; // 빈 카테고리는 숨김
                    const active = categoryFilter === cat.id;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setCategoryFilter(cat.id)}
                        className={`shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] font-medium border transition-all ${
                          active
                            ? "bg-burgundy-600 text-white border-burgundy-600 shadow-sm"
                            : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50 hover:border-stone-300"
                        }`}
                        data-testid={`timeline-category-${cat.id}`}
                      >
                        <span>{cat.emoji}</span>
                        <span>{cat.label}</span>
                        <span className={`tabular-nums text-[11px] font-bold ${active ? "text-white/90" : "text-stone-400"}`}>
                          {count}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {filteredPosts.length === 0 ? (
              <Empty label={
                posts.length === 0
                  ? (isMe ? "아직 작성한 이야기가 없어요." : "아직 이야기가 없네요.")
                  : `이 카테고리에는 아직 이야기가 없어요.`
              } />
            ) : (
              <div className="space-y-8">
                {groupedPosts.map(([month, items]) => (
                  <section key={month}>
                    <h2 className="font-serif text-base text-stone-500 mb-3 pl-1 flex items-center gap-2">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-burgundy-400" />
                      {month}
                      <span className="text-[11px] text-stone-400 font-sans">· {items.length}개의 이야기</span>
                    </h2>
                    <div className="space-y-4">
                      {items.map((p) => <TimelinePost key={p.id} p={p} />)}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </>
        )}

        {tab === "comments" && (
          comments.length === 0 ? (
            <Empty label={isMe ? "아직 내 글에 달린 댓글이 없어요." : "아직 댓글이 없어요."} />
          ) : (
            <div className="space-y-8">
              {groupedComments.map(([month, items]) => (
                <section key={month}>
                  <h2 className="font-serif text-base text-stone-500 mb-3 pl-1">{month}</h2>
                  <div className="space-y-3">
                    {items.map((c) => <TimelineComment key={c.id} c={c} />)}
                  </div>
                </section>
              ))}
            </div>
          )
        )}

        {tab === "photos" && (
          photoItems.length === 0 ? (
            <Empty label="아직 올린 사진이 없어요." />
          ) : (
            <div className="grid grid-cols-3 gap-1.5" data-testid="timeline-photos-grid">
              {photoItems.map((item, i) => (
                <button
                  key={i}
                  onClick={() => navigate(`/community/post/${item.postId}`)}
                  className="aspect-square overflow-hidden rounded-xl bg-stone-100 group"
                >
                  <img
                    src={item.url.startsWith("http") ? item.url : `${API_URL}${item.url}`}
                    alt=""
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                </button>
              ))}
            </div>
          )
        )}
      </div>

      <DMModal
        open={dmOpen}
        onClose={() => setDmOpen(false)}
        userId={userId}
        userName={profile.username}
      />
      <MembershipGuardModal
        open={!!dmGuard}
        reason={dmGuard || "dm_upgrade"}
        onClose={() => setDmGuard(null)}
      />
    </div>
  );
}
